import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { KioskInfo } from '@/types/kiosk.types';
import type { UploadedDocumentMeta, ClientPageInfo } from '@/types/document.types';
import type { PrintOptions, PrintJob } from '@/types/printJob.types';
import type { PaymentPageState, ConfirmationPageState } from '@/types/payment.types'; 

interface SessionState {
  kiosk: KioskInfo | null;
  kioskToken: string | null;

  uploadedFile: File | null;
  documentMeta: UploadedDocumentMeta | null;
  pageInfo: ClientPageInfo;
  isDetectingPageCount: boolean;

  printJob: PrintJob | null;
  printOptions: PrintOptions | null;

  paymentState: PaymentPageState | null; // NEW: survives refresh
  paymentId: string | null;
  otp: string | null;
  confirmation: ConfirmationPageState | null;
  setConfirmation: (c: ConfirmationPageState | null) => void;

  setKiosk: (kiosk: KioskInfo | null) => void;
  setKioskToken: (token: string | null) => void;
  setUploadedFile: (file: File | null) => void;
  setDocumentMeta: (meta: UploadedDocumentMeta | null) => void;
  setPageInfo: (info: ClientPageInfo) => void;
  setIsDetectingPageCount: (value: boolean) => void;
  setPrintJob: (job: PrintJob | null) => void;
  setPrintOptions: (options: PrintOptions) => void;
  setPaymentState: (state: PaymentPageState | null) => void; // NEW
  setPaymentId: (id: string | null) => void;
  setOtp: (otp: string | null) => void;
  resetSession: () => void;
}

const DEFAULT_PRINT_OPTIONS: PrintOptions = {
  pageRange: { mode: 'all' },
  copies: 1,
  orientation: 'portrait',
  colorMode: 'blackAndWhite',
  duplex: 'simplex',
};

const DEFAULT_PAGE_INFO: ClientPageInfo = { pageCount: null, source: 'pending' };

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      kiosk: null,
      kioskToken: null,
      uploadedFile: null,
      documentMeta: null,
      pageInfo: DEFAULT_PAGE_INFO,
      isDetectingPageCount: false,
      printJob: null,
      printOptions: DEFAULT_PRINT_OPTIONS,
      paymentState: null,
      paymentId: null,
      otp: null,
      confirmation: null,
      setConfirmation: (c) => set({ confirmation: c }),

      setKiosk: (kiosk) => set({ kiosk }), 
      setKioskToken: (token) => set({ kioskToken: token }),
      setUploadedFile: (file) => set({ uploadedFile: file }),
      setDocumentMeta: (meta) => set({ documentMeta: meta }),
      setPageInfo: (info) => set({ pageInfo: info }),
      setIsDetectingPageCount: (value) => set({ isDetectingPageCount: value }),
      setPrintJob: (job) => set({ printJob: job }),
      setPrintOptions: (options) => set({ printOptions: options }),
      setPaymentState: (state) => set({ paymentState: state }),
      setPaymentId: (id) => set({ paymentId: id }),
      setOtp: (otp) => set({ otp }),

      // keeps kiosk + kioskToken so "Print Another" stays on the same kiosk
      resetSession: () =>
        set({
          uploadedFile: null,
          documentMeta: null,
          pageInfo: DEFAULT_PAGE_INFO,
          isDetectingPageCount: false,
          printJob: null,
          printOptions: DEFAULT_PRINT_OPTIONS,
          paymentState: null,
          paymentId: null,
          otp: null,
        }),
    }),
    {
      name: 'kiosk-session',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        kiosk: s.kiosk,
        kioskToken: s.kioskToken,
        paymentState: s.paymentState,
      }),
    }
  )
);